import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NavItem = ({ to, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      `block px-4 py-2 rounded-full text-sm font-medium transition-all duration-300
      ${isActive 
        ? "bg-aseBlue text-white shadow-[0_0_15px_rgba(0,112,243,0.5)]" 
        : "text-gray-300 hover:text-white hover:bg-white/10"}`
    }
  >
    {label}
  </NavLink>
);

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4 pb-2 pointer-events-none">
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`pointer-events-auto flex items-center justify-between w-full max-w-5xl px-4 py-3 rounded-2xl transition-all duration-300 border border-white/5 backdrop-blur-md ${scrolled ? 'bg-black/60 shadow-2xl' : 'bg-black/20'}`}
      >
        {/* Left Side: Logo + Brand Name */}
        <Link 
          to="/" 
          className="flex items-center gap-3 group"
          onClick={() => setIsMenuOpen(false)}
        >
          <div className="relative">
            <img
              src="/logo.png"
              alt="ALLIED SOFTWARE ENGINEERS (PRIVATE) LIMITED"
              className="h-10 w-10 rounded-lg shadow-lg relative z-10"
            />
            <div className="absolute inset-0 bg-aseBlue rounded-lg blur-md opacity-0 group-hover:opacity-50 transition duration-300"></div>
          </div>
          <span className="text-sm lg:text-base font-display font-bold tracking-tight text-white hidden md:block">
            ALLIED SOFTWARE ENGINEERS (PRIVATE) LIMITED
          </span>
        </Link>

        {/* Right Side: Desktop Navigation */}
        <div className="hidden md:flex gap-1 items-center bg-white/5 p-1 rounded-full border border-white/5">
          <NavItem to="/" label="Home" />
          <NavItem to="/vision" label="Vision" />
          <NavItem to="/team" label="Team" />
          <NavItem to="/careers" label="Careers" />
          <NavItem to="/contact" label="Contact" />
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-gray-300 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-auto absolute top-full left-4 right-4 mt-2 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl md:hidden"
          >
            <div className="flex flex-col gap-2">
              <NavItem to="/" label="Home" onClick={() => setIsMenuOpen(false)} />
              <NavItem to="/vision" label="Vision" onClick={() => setIsMenuOpen(false)} />
              <NavItem to="/team" label="Our Team" onClick={() => setIsMenuOpen(false)} />
              <NavItem to="/careers" label="Careers" onClick={() => setIsMenuOpen(false)} />
              <NavItem to="/contact" label="Contact" onClick={() => setIsMenuOpen(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}